/**
 * Api handles for secure saving and not so secure loading of the data from the backend.
 */

import { reverse } from "ramda";
import stringify from "json-stable-stringify";
import MD5 from "md5";
import axios from "axios";
import { getChatEventUrl } from "../store";
import { getScreenLayout, urlConfig } from "./utils";

const secretParts = ["6vC24bTu4L", "la05L2pS3V", "0PGsx1waa", "9QMt4", "R2O", "R8JD4", "PSCXSzK9", "zWkJ"];

export const UserEvents = {
  PAGE_VIEW: "PAGE_VIEW",
  PAGE_CLOSED: "PAGE_CLOSED",
  CHAT_OPEN: "CHAT_OPEN",
  CHAT_CLOSE: "CHAT_CLOSE",

  GALLERY_TAB_SWITCHED: "GALLERY_TAB_SWITCHED",

  OPEN_GALLERY: "OPEN_GALLERY",
  OPEN_FLOORPLANS: "OPEN_FLOORPLANS",
  OPEN_IFRAME: "OPEN_IFRAME",
  OPEN_YOUTUBE: "OPEN_YOUTUBE",

  CLOSE_GALLERY: "CLOSE_GALLERY",
  CLOSE_FLOORPLANS: "CLOSE_FLOORPLANS",
  CLOSE_IFRAME: "CLOSE_IFRAME",
  CLOSE_YOUTUBE: "CLOSE_YOUTUBE",

  IMAGE_SLIDE_CHANGE: "IMAGE_SLIDE_CHANGE",
  FLOORPLAN_CHANGE: "FLOORPLAN_CHANGE",

  YOUTUBE_VIDEO_STARTED: "YOUTUBE_VIDEO_STARTED",
  YOUTUBE_VIDEO_PAUSED: "YOUTUBE_VIDEO_PAUSED",
  YOUTUBE_VIDEO_ENDED: "YOUTUBE_VIDEO_ENDED",
  YOUTUBE_TIME_WATCHED: "YOUTUBE_TIME_WATCHED",

  TEXT_SELECTED: "TEXT_SELECTED",

  LINK_CLICKED: "LINK_CLICKED",

  VIEWED_PRICING: "VIEWED_PRICING",
  HISTORY_OPENED: "HISTORY_OPENED",
};

export const UserEventsAPI = {
  /**
   * Use `secretParts` array to do some naive math magic to make nasty hackers life harder.
   */
  _prepareSecret(): string {
    const secretPartsCopy = [...secretParts];
    const partsToReverse = [1, 2, 5, 7];
    partsToReverse.forEach((i) => (secretPartsCopy[i] = reverse(secretPartsCopy[i])));
    return secretPartsCopy.join("");
  },

  /**
   * Calculate the hash string based on the provided data object and the build in secret strings.
   * @param data
   */
  _calcHash(data: Object): string {
    const dataStr = stringify(data);
    return MD5(dataStr + this._prepareSecret());
  },

  _waitingEvents: [],

  flushEvents: () => (dispatch) => {
    while (UserEventsAPI._waitingEvents.length) {
      const storedEvent = UserEventsAPI._waitingEvents.shift();
      const [_e, _d] = storedEvent;
      dispatch(UserEventsAPI._send(_e, _d));
    }
  },

  _send: (event, extraData = {}) => (dispatch, getState) => {
    // Not producing useless errors when debugging or developing.
    if (urlConfig.staticConfig) {
      return;
    }
    const state = getState();
    const { uuid } = state;

    if (!uuid) {
      UserEventsAPI._waitingEvents.push([event, extraData]);
      return;
    }
    if (UserEventsAPI._waitingEvents.length) {
      dispatch(UserEventsAPI.flushEvents());
    }
    const url = getChatEventUrl(state);
    const data = { event, extraData, uuid };
    axios
      .post(url, data, {
        headers: { "X-CHECKSUM": UserEventsAPI._calcHash(data) },
      })
      .then((response) => {
        // console.log(response);
      })
      .catch((error) => {
        // console.log(error);
      });
  },

  /**
   * Generic send method.
   */
  send: (event, extraData = {}) => (dispatch, getState) => {
    UserEventsAPI._send(event, extraData)(dispatch, getState);
  },

  /**
   * Send page view event.
   */
  sendPageView: () => (dispatch, getState) => {
    UserEventsAPI._send(UserEvents.PAGE_VIEW, {
      url: window && window.location.href,
    })(dispatch, getState);
  },

  /**
   * Send chat open event.
   */
  sendChatOpen: () => (dispatch, getState) => {
    UserEventsAPI._send(UserEvents.CHAT_OPEN)(dispatch, getState);
  },

  /**
   * Send page closed event.
   */
  sendPageClosed: () => (dispatch, getState) => {
    UserEventsAPI._send(UserEvents.PAGE_CLOSED)(dispatch, getState);
  },

  /**
   * Send image slide change event.
   */
  sendImageSlideChange: (index) => (dispatch, getState) => {
    UserEventsAPI._send(UserEvents.IMAGE_SLIDE_CHANGE, {
      index,
      orientation: getScreenLayout(),
    })(dispatch, getState);
  },

  /**
   * Send floorplan slide change event.
   */
  sendFloorplanChange: (index) => (dispatch, getState) => {
    UserEventsAPI._send(UserEvents.FLOORPLAN_CHANGE, { index })(dispatch, getState);
  },

  sendHistoryOpened: () => (dispatch, getState) => {
    UserEventsAPI._send(UserEvents.HISTORY_OPENED)(dispatch, getState);
  },
};
